import {supportPhoneNumber} from './supportPhoneNumber';

export enum OfferSource {
  all = 'all',
  pdb = 'pdb',
  ocrm = 'ocrm',
  oms = 'oms',
  tds = 'tds',
}

export enum OfferPreferences {
  Favorite = 'F',
  NewOffer = 'N',
  Viewed = 'V',
  TrashedOffer = 'T', // Used only in OCRM Offers
}

export interface BaseOffer {
  id: string;
  title: string; // 2018-01-01T00:00:00 - Looks to be a DateTime ISO
  start: string;
  end: string;
  pref: OfferPreferences;
  propertyList: string[]; // Property codes
  imageUrl?: string;
  description?: string;
  displayid?: string;
}

export const OfferTypeAll = 'All';

export enum OfferType {
  Gaming = 'Gaming',
  Hotel = 'Hotel',
  LoyaltyCredits = 'Loyalty Credits',
  FoodAndDrink = 'Food & Drink',
  Wellness = 'Wellness',
  Entertainment = 'Entertainment',
  EventsAndPerks = 'Events & Perks',
  Golf = 'Golf',
  TravelPackages = 'Travel Packages',
  Transportation = 'Transportation',
  GiftsAndDiscounts = 'Gifts & Discounts',
  Other = 'Other',
}

export interface PDBOffer extends BaseOffer {
  source: OfferSource.pdb;
  status: string;
  ruleId: string;
  /**
   * Only for Air offers. The maximum value of the offer.
   *
   * CAUTION: Do not display to customer!
   */
  customerValue?: any;

  /**
   * Hotel offers can be booked online.
   * Airfare offers can be booked online by the guests casino host,
   * but should not be viewable to the guest online.
   */
  type: OfferType;
  refinedType: OfferType;
}

export enum OCRMOfferPriority {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
  DEFAULT = 'DEFAULT',
}

export interface OCRMLink {
  href: string;
  rel: string;
}

export enum RewardState {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export enum OCRMStatus {
  ACTIVE = 'Active',
  ACCEPTED = 'Accepted',
  OFFERED = 'Offered',
}

export interface OCRMReward {
  category: string;
  propertyList: string[];
  id: number;
  nature: string;
  state: RewardState;
  title: string;
  description: string;
  extendedTitle: string;
  links: {link: OCRMLink[]};
  refinedType: OfferType;
}

export interface OCRMOffer extends BaseOffer {
  source: OfferSource.ocrm;
  displayid?: string;
  status: string;
  ruleId?: string;
  ocrm: {
    priorityCode: OCRMOfferPriority;
    links: {link: OCRMLink[]};
    rewards: OCRMReward[];
  };
  parentId?: string;
}

export type Offer = OCRMOffer | PDBOffer;

export interface Offers {
  [offerId: string]: Offer;
}

export enum OfferRequestStatus {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export interface OffersSuccessHeaders {
  status: OfferRequestStatus.SUCCESS;
}

export interface OfferErrorCodes {
  code?: string; // Seems they only report WCS0001 for this endpoint?
  message?: string; // This is the true error will have to parse message.
}

export interface OffersFailureHeaders {
  status: OfferRequestStatus.FAILURE;
  errors?: OfferErrorCodes[];
}

export type OffersHeaders = OffersSuccessHeaders | OffersFailureHeaders;

export interface OffersResponse {
  header: OffersHeaders;
  offers: Offer[];
  source?: OfferSource;
}

export interface SaveOfferPrefResponse {
  header: OffersHeaders;
}

export const OFFER_GROUP_THRESHOLD = 20;
export const PROPERTY_LIST_MAX_CHARACTER = 22;
export const OFFER_TITLE_MAX_CHARACTER = 40;
export const OFFER_TITLE_CUT_OFF_CHARACTER = 37;

export const OFFER_TERMS_PDB =
  'Caesars Rewards offers are non-transferable and non-negotiable. Offer is only valid at specified casinos. Caesars Rewards Card and valid photo ID must be presented upon redemption. Not responsible for lost or stolen vouchers or coupons.  Offers are based upon availability. Effective July 1, 2017, complimentary rooms booked will be subject to a $50 no-show fee, plus tax if the reservation is not canceled by 6pm on the day of arrival. Excludes bookings made for Caesars Windsor. Additional restrictions may apply. Please see your nearest Caesars Rewards Center for more details.';
export const OFFER_TERMS_PDB_RIN =
  "Harrah's Resort Southern California: Caesars Rewards offers are non-transferable and non-negotiable. Offer is only valid at specified casinos. Caesars Rewards Card and valid photo ID must be presented upon redemption. Not responsible for lost or stolen vouchers or coupons. Offers are based on availability. First night complimentary reservations require a 24-hour cancellation notice. For reservations that no-show or cancel after 12:00am on the date prior to arrival a fee in the amount of $81 will be charged. First night paid room reservations require a 24-hour cancellation notice. For reservations that no-show or cancel after 12:00am on the date prior to arrival a fee in the amount of the first night's room rate and tax will be charged. Package reservations require a 24-hour cancellation notice. For reservations that no-show or cancel after 12:00am on the date prior to arrival a fee in the amount of the total package cost and tax will be charged. The applicable fee will be charged to credit card on file. For Caesars Rewards customers, if credit card is absent, declined or invalid the amount of the applicable fee will be deducted from your Caesars Rewards account. Declined or invalid credit cards may result in cancellation of booking without prior notice.";
export const OFFER_HOW_REDEEM_PDB =
  'To redeem your offer, please follow the instructions on your mail piece or email. A print out of this page is NOT a physical coupon and cannot be used for redemption where a coupon is required and some offers require a physical coupon to redeem.\n\nIf this is a hotel offer and you book it online; you do not need to bring in your physical coupon when you check in.\n\nIt is always best to bring your invite or mail piece with you when you visit.';

export const OFFER_OCRM_ERRORSTATE_MESSAGE = `Unfortunately, a system error occurred and this offer could not be accepted. Show this screen to a Caesars employee if you're on property, or contact support at ${supportPhoneNumber}.`;

// OCRM offer structure returned from tds
export interface TDSOffer {
  linkList: TDSLinkList;
  validProperties: TDSValidProperties;
  validMarketLists: TDSValidMarketLists;
  rewardList: TDSRewardList;
  qualifierId: number;
  state: string;
  title: string;
  startDate: string;
  expiration: string;
  timezone: string;
  offerCode: string;
  priorityCode: string;
  ruleId: string;
}

export interface TDSLinkList {
  link: TDSLink[];
}

export interface TDSLink {
  content: any[];
  href: string;
  rel: string;
  otherAttributes: TDSOtherAttributes;
}

export interface TDSOtherAttributes {}

export interface TDSRewardList {
  reward: TDSReward[];
}

export interface TDSReward {
  linkList: TDSLinkList;
  detail: string;
  propertyDetails: TDSMarketListDetail;
  rewardId: number;
  title: string;
  category: string;
  rewardNature: string;
  rewardState: string;
}

export interface TDSMarketListDetail {
  code: string;
  description: string;
  otherAttributes: TDSOtherAttributes;
}

export interface TDSValidMarketLists {
  marketListDetails: TDSMarketListDetail[];
}

export interface TDSValidProperties {
  propertyDetails: TDSMarketListDetail[];
}
export const OFFER_TERMS_ERROR = `Sorry, we are unable to display the Terms & Conditions. Please refresh or try again later. If the issue persists, contact support at ${supportPhoneNumber}.`;

export const OFFER_ACCEPT_ERROR =
  'Unfortunately there was an issue with accepting your offer. Please reload and try again.';

export const OFFER_CARDS_MAX_CONTAINER_WIDTH = 1018;

export const REFRESH_BANNER_HEADING = 'NEW OFFERS UNLOCKED!';
